<template>
    <div class="app-container">
        <template v-if="isAuthenticated">
            <global-banner
                :is-impersonating="isImpersonating"
                :home-subscription="homeSubscription" />
            <div
                id="screen-reader-global-messages"
                class="sr-only"
                role="status"
                aria-atomic="true"
                :aria-live="ariaLiveMode">
                {{ activeScreenReaderMessage }}
            </div>
            <div class="app-content">
                <side-nav v-if="showSideNav" />
                <div id="main-container">
                    <top-nav
                        v-if="showNavItems"
                        :is-impersonating="isImpersonating"
                        :home-subscription="homeSubscription" />
                    <vue-wrapper />
                </div>
            </div>
            <impersonation-switcher v-if="hasSuperAccess" />
        </template>
        <template v-else>
            <router-view />
        </template>
        <collaboration-integration v-if="hasCommentsAccess" />
    </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { i18n } from '@/utils/i18n';
import { showCelebrationModalOnPendoReady } from '@/free/free.utils.js';
import { buildUnauthenticatedLoginRedirectRoute } from '@/vue-router/route-guards.utils';
import { isOnASettingsPage, isOnReplayPlayerPage, isOnConversationalAiPage } from '@/vue-router/route.utils';
import { watch } from 'vue';
import { useRoute } from 'vue-router';
import GlobalBanner from '@/global-banner/global-banner.vue';
import SideNav from '@/side-nav/side-nav.vue';
import VueWrapper from '@/vue-router/vue-wrapper.vue';
import TopNav from '@/top-nav/top-nav.vue';
import CollaborationIntegration from '@/collaboration/integration/collaboration-integration.vue';
import ImpersonationSwitcher from '@/super/impersonation-switcher/impersonation-switcher.vue';
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';
import { useHeadingScope } from '@pendo/components';

export default {
    name: 'App',
    i18n,
    components: {
        SideNav,
        VueWrapper,
        TopNav,
        GlobalBanner,
        CollaborationIntegration,
        ImpersonationSwitcher,
    },
    setup () {
        useHeadingScope({ level: 1 });

        const { initializeDarkMode } = useDarkMode();
        const { activeScreenReaderMessage, ariaLiveMode, clearScreenReaderQueue: clearQueue } = useAriaLiveRegistry();
        const route = useRoute();

        watch(() => route.fullPath, () => {
            clearQueue();
        });

        return { initializeDarkMode, activeScreenReaderMessage, ariaLiveMode };
    },
    data () {
        return {
            removeCompletedLoadWatch: null,
        };
    },
    computed: {
        ...mapGetters({
            hasCompletedUserLoad: 'auth/hasCompletedUserLoad',
            isAuthenticated: 'auth/isAuthenticated',
            isImpersonating: 'auth/isImpersonating',
            hasSuperAccess: 'auth/hasSuperAccess',
            hasOnlyTrainingSub: 'auth/hasOnlyTrainingSub',
            isAutoProvisionedTrialExpired: 'subscription/isAutoProvisionedTrialExpired',
            isPendoFree: 'subscription/isPendoFree',
            getFeatureFlag: 'subscription/getFeatureFlag',
            subscriptionList: 'subscription/list',
            hasCollaborationAccess: 'collaboration/hasCollaborationAccess',
            hasListenCommentsAccess: 'collaboration/hasListenCommentsAccess',
            hasSegmentFlag: 'auth/hasSegmentFlag',
        }),
        ...mapState({
            activeSubscription: (state) => state.subscription.active,
        }),
        homeSubscription () {
            if (!this.subscriptionList?.length) return null;

            return this.subscriptionList[0];
        },
        hasCommentsAccess () {
            return this.hasCollaborationAccess || this.hasListenCommentsAccess;
        },
        eventCount () {
            return this.activeSubscription?.eventCount || 0;
        },
        onGlobalSettingsPage () {
            return isOnASettingsPage(this.$route?.path) && this.$route.query.globalSettings !== undefined;
        },
        onOauthDetailsPage () {
            return this.$route.name === 'OAuthAuthorize';
        },
        onReplayPlayerPage () {
            return isOnReplayPlayerPage(this.$route?.path);
        },
        onSetupPage () {
            return this.$route?.name === 'Setup';
        },
        onInactiveSubPage () {
            return this.$route?.name === 'Inactive';
        },
        onInvitePages () {
            return this.$route?.name === 'InvitationError' || this.$route?.name === 'Confirmation';
        },
        onConversationalAiPage () {
            return isOnConversationalAiPage(this.$route?.path);
        },
        onRegisterUserPages () {
            return (
                this.$route?.name === 'PostRegisterSteps' ||
                this.$route?.name === 'CompleteUser' ||
                this.$route?.name === 'Register'
            );
        },
        onForgotPasswordPages () {
            return (
                this.$route?.name === 'ExpiredLink' ||
                this.$route?.name === 'ResetPassword' ||
                this.$route?.name === 'ForgotPasswordForm'
            );
        },
        onExternalFeedbackFormPage () {
            return this.$route?.name === 'ValidateFeedbackFormExternal';
        },
        showNavItems () {
            return (
                !this.onGlobalSettingsPage &&
                !this.onOauthDetailsPage &&
                !this.onReplayPlayerPage &&
                !this.onSetupPage &&
                !this.onConversationalAiPage &&
                !this.onInactiveSubPage &&
                !this.onInvitePages &&
                !this.onRegisterUserPages &&
                !this.onForgotPasswordPages &&
                !this.onExternalFeedbackFormPage &&
                this.isAuthenticated
            );
        },
        showSideNav () {
            return !this.isAgentModeActive && this.showNavItems;
        },
        isAgentModeActive () {
            const route = this.$route.name;
            const hasAgentMode = this.hasSegmentFlag('askPendo');

            return hasAgentMode && (route === 'AgentMode' || route === 'AgentModeConversation');
        },
    },
    mounted () {
        this.scrollToHashAnchor();
        this.removeCompletedLoadWatch = this.$watch(
            () => this.hasCompletedUserLoad,
            this.onCompletedUserLoad.bind(this),
            { immediate: true },
        );
    },
    methods: {
        ...mapActions({
            initUserJourney: 'userJourney/init',
            updateEventCount: 'subscription/updateEventCount',
            updateSetting: 'subscription/updateSetting',
        }),
        scrollToHashAnchor () {
            const anchor = window.location.hash;
            if (anchor) {
                const anchorElement = document.querySelector(anchor);
                if (anchorElement) anchorElement.scrollIntoView();
            }
        },
        onCompletedUserLoad (hasCompletedLoading) {
            if (!hasCompletedLoading) return;

            if (!this.isAuthenticated) {
                if (this.$route?.meta?.skipLoginRedirect) return;

                const loginRoute = buildUnauthenticatedLoginRedirectRoute();
                this.$router.replace(loginRoute);

                return;
            }

            this.initializeDarkMode();

            if (this.hasOnlyTrainingSub) {
                this.$router.replace({ name: 'Inactive' });

                return;
            }

            if (this.isAutoProvisionedTrialExpired) {
                this.$router.replace({ name: 'TrialExpired' });

                return;
            }

            // if the subscription should lock users out until they've installed the snippet,
            // send them directly to the /setup route
            if (!this.hasSuperAccess && !this.getFeatureFlag('allowSignedMetadata')) {
                const noEvents = this.eventCount === 0;
                const canBypassLockScreen = this.getFeatureFlag('bypassLockScreen');

                if (noEvents && !canBypassLockScreen) {
                    const { setupKey } = this.activeSubscription;

                    return {
                        name: 'Setup',
                        query: {
                            setupKey,
                        },
                    };
                }
            }

            if (this.$route.name === 'TrialExpired') {
                this.$router.replace({ name: 'Home' });

                return;
            }

            if (this.isPendoFree && this.eventCount === 0) {
                this.pollForInstallSuccess();
            }

            // because we eager watch for auth loading, sometimes its already done the first
            // time this watch gets called. In that case, this.removeCompletedLoadWatch hasn't
            // actually been set yet, so queue the unwatch
            this.$nextTick(() => {
                this.removeCompletedLoadWatch();
                delete this.removeCompletedLoadWatch;
            });
        },
        pollForInstallSuccess () {
            if (this.eventCount > 0) {
                showCelebrationModalOnPendoReady(() => {
                    this.updateSetting({
                        setting: 'seenCelebrationModal',
                        value: 'true',
                        label: 'seenCelebrationModal',
                    });
                });

                this.initUserJourney();
            } else {
                setTimeout(async () => {
                    await this.updateEventCount();
                    this.pollForInstallSuccess();
                }, 60000);
            }
        },
    },
};
</script>

<style lang="less" scoped>
.app-content {
    display: flex;
    position: relative;
    overflow: hidden;
    flex-grow: 1;
}
</style>
